/**
 * /legal/terms — Terms and Conditions.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { LegalLayout } from '../../components/layouts/legal-layout';

export const Route = createFileRoute('/legal/terms')({
  component: TermsPage,
});

const TOC = [
  { anchor: 'acceptance',          label: 'Acceptance' },
  { anchor: 'account',             label: 'Account responsibilities' },
  { anchor: 'data-processor',      label: 'Our role as data operator (POPIA)' },
  { anchor: 'tenant-obligations',  label: 'Tenant responsibilities' },
  { anchor: 'fees',                label: 'Fees and billing' },
  { anchor: 'termination',         label: 'Termination' },
  { anchor: 'disputes',            label: 'Disputes' },
  { anchor: 'governing-law',       label: 'Governing law' },
];

function TermsPage() {
  return (
    <LegalLayout heading="Terms and Conditions." lastUpdated="7 May 2026" toc={TOC}>
      <h2 id="acceptance">Acceptance</h2>
      <p>
        By accessing or using the Elreign platform (<strong>the Service</strong>), you agree to be
        bound by these Terms of Service (<strong>Terms</strong>). If you are accessing the Service on
        behalf of a firm or organisation (<strong>Tenant</strong>), you represent that you have
        authority to bind that entity to these Terms.
      </p>
      <p>
        If you do not agree to these Terms, you may not use the Service.
      </p>

      <h2 id="account">Account responsibilities</h2>
      <p>You are responsible for:</p>
      <ul>
        <li>
          Maintaining the confidentiality of your credentials, including your authenticator device
          and backup codes.
        </li>
        <li>
          All activity that occurs under your account, whether authorised by you or not.
        </li>
        <li>
          Notifying Elreign promptly if you believe your account has been compromised, at{' '}
          <a href="mailto:security@elreign.io" style={{ color: 'var(--primary-600)' }}>
            security@elreign.io
          </a>
          .
        </li>
        <li>
          Ensuring that information you provide is accurate, current, and complete.
        </li>
      </ul>
      <p>
        Elreign is not liable for any loss arising from your failure to comply with these
        account security obligations.
      </p>

      <h2 id="data-processor">Our role as data operator (POPIA)</h2>
      <p>
        In South African law, the fiduciary firm (Tenant) is the <strong>responsible party</strong>{' '}
        for the personal information of its clients. Elreign acts as the <strong>operator</strong>,
        processing personal information on the Tenant&apos;s behalf and on its instructions.
      </p>
      <p>
        Elreign will only process personal information to the extent necessary to provide the
        Service, and will not use it for any purpose inconsistent with the Tenant&apos;s instructions
        or with applicable law. Where required by law to disclose personal information, Elreign will
        notify the Tenant to the extent permitted by law.
      </p>
      <p>
        Further detail on data processing is in our{' '}
        <Link to="/legal/privacy" style={{ color: 'var(--primary-600)' }}>
          Privacy Policy
        </Link>
        .
      </p>

      <h2 id="tenant-obligations">Tenant responsibilities</h2>
      <p>
        As responsible party, the Tenant is responsible for:
      </p>
      <ul>
        <li>
          Ensuring that it has a lawful basis to collect and process the personal information
          of trust clients and beneficiaries before onboarding them to the Service.
        </li>
        <li>
          Maintaining its own PAIA manual, information officer appointment, and POPIA
          compliance programme.
        </li>
        <li>
          Ensuring that all users it provisions on the Service are properly authorised to access
          the relevant trust records.
        </li>
        <li>
          Using the Service only for lawful trust administration purposes and not for any
          purpose that would violate applicable law.
        </li>
      </ul>

      <h2 id="fees">Fees and billing</h2>
      <p>
        Fees are as published on the pricing page or as agreed in a separate order form. Where fees
        are billed monthly, payment is due within 15 days of invoice. Elreign reserves the right to
        suspend access if invoices remain unpaid after 30 days, with written notice.
      </p>
      <p>
        Elreign may adjust fees on 60 days written notice. If you do not accept an adjustment, you
        may terminate the Service under the termination provisions below.
      </p>

      <h2 id="termination">Termination</h2>
      <p>
        Either party may terminate these Terms with 30 days written notice. Elreign may terminate
        immediately for material breach, non-payment, or where continued use poses a legal or
        security risk.
      </p>
      <p>
        On termination, Elreign will make your data available for export for 30 days, after which
        it will be deleted subject to mandatory retention obligations under FICA s23 (5-year
        minimum).
      </p>

      <h2 id="disputes">Disputes</h2>
      <p>
        The parties will attempt to resolve any dispute in good faith within 30 days of written
        notice. If the dispute is not resolved, it may be referred to expedited arbitration under
        the rules of the Arbitration Foundation of Southern Africa (<strong>AFSA</strong>), with a
        single arbitrator appointed by AFSA. The place of arbitration is Cape Town.
      </p>

      <h2 id="governing-law">Governing law</h2>
      <p>
        These Terms are governed by the laws of the Republic of South Africa. The parties consent
        to the jurisdiction of the Western Cape Division of the High Court as the court of first
        instance, without prejudice to the arbitration clause above.
      </p>
    </LegalLayout>
  );
}
